
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Clock, XCircle } from "lucide-react";
import { ExpenseReport, Profile, displayDate } from "@/types/ExpenseReport";

interface ExpenseReportDetailsProps {
  report: ExpenseReport;
  generatedByUser: Profile | null;
  approvedByUser: Profile | null;
  isManager: boolean;
  isOwner: boolean;
  isDraft: boolean;
  isRejected: boolean;
  canResubmit: boolean;
  isResubmitting: boolean;
  shortReportId?: string;
  onApprove: () => void;
  onReject: () => void;
  onSubmit: () => void;
}

export const ExpenseReportDetails = ({
  report,
  generatedByUser,
  approvedByUser,
  isManager,
  isOwner,
  isDraft,
  isRejected,
  canResubmit,
  isResubmitting,
  shortReportId,
  onApprove,
  onReject,
  onSubmit
}: ExpenseReportDetailsProps) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <CardTitle className="text-xl">Report Details</CardTitle>
            {shortReportId && (
              <CardDescription>
                Expense Report #{shortReportId}
              </CardDescription>
            )}
          </div>
          <div className="flex flex-row items-center gap-2">
            {report.status === 'approved' && (
              <div className="flex items-center text-green-600 gap-1 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span>Approved</span>
              </div>
            )}
            {report.status === 'pending' && (
              <div className="flex items-center text-amber-600 gap-1 text-sm">
                <Clock className="h-4 w-4" />
                <span>Pending</span>
              </div>
            )}
            {report.status === 'rejected' && (
              <div className="flex items-center text-red-600 gap-1 text-sm">
                <XCircle className="h-4 w-4" />
                <span>Rejected</span>
              </div>
            )}
            {report.status === 'draft' && (
              <div className="flex items-center text-blue-600 gap-1 text-sm">
                <Clock className="h-4 w-4" />
                <span>Draft</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium">Date Range</p>
            <p className="text-sm text-muted-foreground">
              {displayDate(report.date_from)} - {displayDate(report.date_to)}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium">Total Amount</p>
            <p className="text-sm text-muted-foreground">
              {report.total_amount.toFixed(2)} {report.currency}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium">Created By</p>
            <p className="text-sm text-muted-foreground">
              {generatedByUser ? `${generatedByUser.first_name} ${generatedByUser.last_name}` : 'Loading...'}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium">Created On</p>
            <p className="text-sm text-muted-foreground">
              {displayDate(report.created_at)}
            </p>
          </div>
          {report.approved_by && (
            <>
              <div>
                <p className="text-sm font-medium">Approved By</p>
                <p className="text-sm text-muted-foreground">
                  {approvedByUser ? `${approvedByUser.first_name} ${approvedByUser.last_name}` : 'Loading...'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Approved On</p>
                <p className="text-sm text-muted-foreground">
                  {report.approval_date ? displayDate(report.approval_date) : 'N/A'}
                </p>
              </div>
            </>
          )}
        </div>
        
        {isRejected && report.rejection_comments && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
            <p className="text-sm text-red-700 mt-1">{report.rejection_comments}</p>
          </div>
        )}
      </CardContent>
      
      {((isManager && report.status === 'pending') || canResubmit) && (
        <CardFooter className="flex justify-end gap-2 flex-wrap">
          {isManager && report.status === 'pending' && (
            <>
              <Button variant="outline" onClick={onReject}>
                Reject
              </Button>
              <Button variant="success" onClick={onApprove}>
                Approve
              </Button>
            </>
          )}
          {canResubmit && (
            <Button 
              onClick={onSubmit} 
              disabled={isResubmitting}
            >
              {isResubmitting ? "Submitting..." : "Submit for Approval"}
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};
